import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import VinylCard from "../components/VinylCard";
import Filters from "../components/Filters";

const PER_PAGE = 24;

export default function Catalog() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], pagination: { total: 0, pages: 1, page: 1 } });
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const paramsObj = useMemo(() => {
    const obj = {};
    for (const [k, v] of searchParams.entries()) obj[k] = v;
    if (!obj.page) obj.page = 1;
    if (!obj.sort) obj.sort = "recent";
    return obj;
  }, [searchParams]);

  const updateParams = (next) => {
    const clean = {};
    Object.entries(next).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined && v !== "recent" && v !== 1) {
        clean[k] = String(v);
      }
    });
    if (next.sort && next.sort !== "recent") clean.sort = next.sort;
    if (next.page && next.page > 1) clean.page = next.page;
    setSearchParams(clean);
  };

  useEffect(() => {
    api.get("/filters/facets").then((r) => setFacets(r.data)).catch(() => setFacets({}));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { ...paramsObj, per_page: PER_PAGE };
    api.get("/listings", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], pagination: { total: 0, pages: 1, page: 1 } }))
      .finally(() => setLoading(false));
  }, [paramsObj]);

  const goPage = (p) => updateParams({ ...paramsObj, page: p });

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-8 md:pt-12" data-testid="catalog-page">
      <div className="flex items-baseline justify-between mb-4 md:mb-5 border-b border-border pb-5">
        <div>
          <h1 className="text-xl md:text-2xl font-medium text-foreground">
            {t("catalog.title")}
          </h1>
          <p className="text-xs text-muted-foreground mt-1.5">
            {data.pagination.total} {data.pagination.total === 1 ? t("catalog.disc") : t("catalog.discs")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="md:hidden inline-flex items-center gap-2 text-sm border border-border px-3 py-1.5 hover:border-foreground"
          data-testid="catalog-open-filters"
        >
          <SlidersHorizontal size={13} /> {t("catalog.openFilters")}
        </button>
      </div>

      {/* Quick filter chips */}
      <div className="flex flex-wrap gap-2 mb-6 md:mb-8" data-testid="catalog-chips">
        <ChipButton
          active={paramsObj.max_price === "20"}
          onClick={() => updateParams({ ...paramsObj, page: 1, max_price: paramsObj.max_price === "20" ? "" : "20" })}
          testid="chip-under20"
        >
          {t("catalog.chip.under20")}
        </ChipButton>
        <ChipButton
          active={paramsObj.max_price === "30"}
          onClick={() => updateParams({ ...paramsObj, page: 1, max_price: paramsObj.max_price === "30" ? "" : "30" })}
          testid="chip-under30"
        >
          {t("catalog.chip.under30")}
        </ChipButton>
        <ChipButton
          active={paramsObj.sort === "recent"}
          onClick={() => updateParams({ ...paramsObj, page: 1, sort: "recent" })}
          testid="chip-recent"
        >
          {t("catalog.chip.newWeek")}
        </ChipButton>
        {(paramsObj.max_price || (paramsObj.sort && paramsObj.sort !== "recent") || paramsObj.style || paramsObj.genre || paramsObj.q) && (
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 ml-auto"
            data-testid="chip-clear"
          >
            {t("catalog.chip.clear")}
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-8 md:gap-10">
        <div className="hidden md:block">
          <Filters facets={facets} params={paramsObj} setParams={updateParams} />
        </div>

        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-square bg-[hsl(var(--muted))] animate-pulse" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground text-sm border border-dashed border-border" data-testid="catalog-empty">
              {t("catalog.empty")}
            </div>
          ) : (
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 gap-y-8 md:gap-x-5 md:gap-y-10"
              data-testid="catalog-grid"
            >
              {data.items.map((it) => (
                <VinylCard key={it.listing_id} item={it} />
              ))}
            </div>
          )}

          {data.pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-12 border-t border-border pt-5 text-sm" data-testid="catalog-pagination">
              <button
                onClick={() => goPage(Math.max(1, Number(paramsObj.page) - 1))}
                disabled={Number(paramsObj.page) <= 1}
                className="disabled:opacity-30 text-muted-foreground hover:text-foreground"
                data-testid="pagination-prev"
              >
                {t("catalog.prev")}
              </button>
              <span className="text-muted-foreground text-xs">
                {t("catalog.page")} {paramsObj.page} / {data.pagination.pages}
              </span>
              <button
                onClick={() => goPage(Math.min(data.pagination.pages, Number(paramsObj.page) + 1))}
                disabled={Number(paramsObj.page) >= data.pagination.pages}
                className="disabled:opacity-30 text-muted-foreground hover:text-foreground"
                data-testid="pagination-next"
              >
                {t("catalog.next")}
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-background overflow-y-auto" data-testid="catalog-drawer">
          <div className="px-5 py-6">
            <Filters
              facets={facets}
              params={paramsObj}
              setParams={updateParams}
              onClose={() => setDrawerOpen(false)}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-8 w-full py-3.5 bg-foreground text-background text-sm"
              data-testid="catalog-apply-filters"
            >
              {t("catalog.showResults")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChipButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 border transition-colors ${
        active
          ? "bg-foreground text-background border-foreground"
          : "border-border text-foreground hover:border-foreground"
      }`}
    >
      {children}
    </button>
  );
}
